<template>
  <div class="merchant-container">
    <div class="side-nav">
      <MerchantSideNav />
      <MenuToggle />
    </div>
    <div class="merchant-body">
      <header>
        <MerchantHeader />
      </header>
      <div class="dashboard-content">
        <div class="row mt-5">
          <h3>KYC Documents</h3>
        </div>
        <div class="row">
          <div class="col-12 pl-0">
            <div class="card-style">
              <div class="row justify-content-between">
                <div class="col-12 col-lg-6 col-md-6">
                  <span>Upload your KYC Documents</span>
                </div>
                <div class="col-12 col-lg-6 col-md-6 text-lg-right" v-if="isKycVerified">
                  <span style="color: #00a800;">KYC Verified</span>
                </div>
              </div>
              <div class="row">
                <div class="col-12">
                  Please provide your identity document with both front and back
                  images for verification.
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Document Upload Form -->
        <div class="row" v-if="!isKycVerified">
          <div class="card-style">
            <b-form
              @submit.stop.prevent="submitKYCDocument"
              enctype="multipart/form-data"
            >
              <h3>{{ isEditing ? "Update Document" : "Add Document" }}</h3>
              <div class="row">
                <div class="col-12">
                  Please select document type and upload both front and back
                  images for identity verification.
                </div>
              </div>

              <!-- Document Type and Document Number in 1 Row -->
              <div class="row">
                <div class="col-12 col-lg-6">
                  <h5>Select Document Type *</h5>
                </div>
                <div class="col-12 col-lg-6">
                  <h5>Document Number (Optional)</h5>
                </div>
              </div>
              <div class="row align-items-center">
                <!-- Document Type -->
                <div class="mb-3 col-12 col-lg-6 position-relative">
                  <b-form-select
                    v-model="formData.document_type"
                    :options="document_options"
                  >
                    <template v-slot:first>
                      <b-form-select-option :value="null" disabled
                        >Select Document Type</b-form-select-option
                      >
                    </template>
                  </b-form-select>
                  <span class="position-absolute">Document Type</span>
                  <span class="text-danger" v-if="errors.document_type">
                    {{ errors.document_type }}
                  </span>
                  <small class="text-muted" v-if="typesLoading">
                    Loading document types...
                  </small>
                </div>

                <!-- Document Number -->
                <div class="mb-3 col-12 col-lg-6 position-relative">
                  <b-form-input
                    v-model="formData.document_number"
                    type="text"
                    placeholder="Enter document number (e.g., passport/ID number)"
                    class="form-control"
                  ></b-form-input>
                  <span class="position-absolute">Document Number</span>
                </div>
              </div>

              <!-- Front Image and Back Image in 1 Row -->
              <div class="row">
                <div class="col-12 col-lg-6">
                  <h5>Front Image {{ isEditing ? "(leave empty to keep)" : "*" }}</h5>
                </div>
                <div class="col-12 col-lg-6">
                  <h5>Back Image {{ backImageRequired ? "*" : "(Optional)" }}</h5>
                </div>
              </div>
              <div class="row align-items-center">
                <!-- Front Image -->
                <div class="mb-3 col-12 col-lg-6 position-relative">
                  <input
                    type="file"
                    ref="frontImageFile"
                    accept="image/png, image/jpeg, image/jpg"
                    @change="onFrontImageChange"
                    class="form-control"
                  />
                  <span class="position-absolute">Front Image (JPG/PNG)</span>
                  <span class="text-danger" v-if="errors.front_image">
                    {{ errors.front_image }}
                  </span>
                  <small class="text-muted" v-if="formData.front_image">
                    &#10003; Front image selected
                  </small>
                </div>

                <!-- Back Image -->
                <div class="mb-3 col-12 col-lg-6 position-relative">
                  <input
                    type="file"
                    ref="backImageFile"
                    accept="image/png, image/jpeg, image/jpg"
                    @change="onBackImageChange"
                    class="form-control"
                  />
                  <span class="position-absolute">Back Image (JPG/PNG)</span>
                  <span class="text-danger" v-if="errors.back_image">
                    {{ errors.back_image }}
                  </span>
                  <small class="text-muted" v-if="formData.back_image">
                    &#10003; Back image selected
                  </small>
                </div>
              </div>

              <!-- Submit Button -->
              <div class="row">
                <div class="col">
                  <b-button
                    variant="primary"
                    type="submit"
                    class="ml-1"
                    :disabled="isLoading"
                  >
                    <b-spinner small v-if="isLoading" class="mr-2"></b-spinner>
                    {{ submitLabel }}
                  </b-button>
                  <b-button
                    variant="secondary"
                    class="ml-2"
                    v-if="isEditing"
                    :disabled="isLoading"
                    @click="cancelEdit"
                  >
                    Cancel
                  </b-button>
                </div>
              </div>
            </b-form>
          </div>
        </div>

        <!-- Uploaded Documents View -->
        <div class="row mb-5">
          <div class="col-12 pl-0">
            <div class="card-style">
              <div class="col-12 pl-0">
                <h3>Uploaded Documents</h3>
                <div
                  class="row"
                  v-if="uploadedDocuments && uploadedDocuments.length > 0"
                >
                  <div
                    v-for="(doc, index) in uploadedDocuments"
                    :key="doc.id || index"
                    class="mb-3 col-12 col-lg-6"
                  >
                    <div class="card">
                      <div class="card-header">
                        <h6>
                          {{ documentTypeLabel(doc) }}
                          <span v-if="doc.document_number" class="ml-2">
                            (#{{ doc.document_number }})
                          </span>
                          <span
                            v-if="documentStatus(doc)"
                            class="doc-status ml-2"
                            :class="isDocVerified(doc) ? 'is-verified' : 'is-pending'"
                          >
                            {{ documentStatus(doc) }}
                          </span>
                        </h6>
                      </div>
                      <div class="card-body">
                        <!-- Front Image -->
                        <div class="row mb-3" v-if="doc.front_image">
                          <div class="col-12">
                            <label>Front Image:</label>
                            <div class="document-image-container">
                              <img
                                :src="resolveImage(doc.front_image)"
                                :alt="documentTypeLabel(doc) + ' - Front'"
                                class="img-fluid"
                              />
                            </div>
                          </div>
                        </div>

                        <!-- Back Image -->
                        <div class="row mb-3" v-if="doc.back_image">
                          <div class="col-12">
                            <label>Back Image:</label>
                            <div class="document-image-container">
                              <img
                                :src="resolveImage(doc.back_image)"
                                :alt="documentTypeLabel(doc) + ' - Back'"
                                class="img-fluid"
                              />
                            </div>
                          </div>
                        </div>

                        <!-- Action Buttons -->
                        <div class="row" v-if="!isDocVerified(doc) && !isKycVerified">
                          <div class="col-12">
                            <button
                              type="button"
                              class="btn btn-sm btn-primary mr-2"
                              @click="editDocument(doc)"
                              :disabled="isLoading"
                            >
                              <span class="material-icons-outlined">edit</span>
                              Update
                            </button>
                            <button
                              type="button"
                              class="btn btn-sm btn-danger"
                              @click="deleteDocument(doc)"
                              :disabled="isLoading"
                            >
                              <span class="material-icons-outlined">delete</span>
                              Delete
                            </button>
                          </div>
                        </div>
                        <div class="row" v-else>
                          <div class="col-12">
                            <small class="text-muted">
                              Verified documents cannot be changed. Please contact the Admin.
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div v-else>
                  <p>
                    No documents uploaded yet. Please upload your KYC documents
                    for verification.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <footer>
      <MerchantFooter />
    </footer>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import MerchantSideNav from "./MerchantSideNav";
import MerchantHeader from "./MerchantHeader";
import MerchantFooter from "./MerchantFooter";
import MenuToggle from "./MenuToggle";

export default {
  name: "MerchantDocuments",
  components: {
    MerchantSideNav,
    MerchantHeader,
    MerchantFooter,
    MenuToggle,
  },
  data() {
    return {
      isLoading: false,
      typesLoading: false,
      editingId: null,
      formData: {
        document_type: null,
        document_number: "",
        front_image: null,
        back_image: null,
      },
      errors: {
        document_type: "",
        front_image: "",
        back_image: "",
      },
    };
  },
  computed: {
    ...mapState([
      "profile",
      "message",
      "errormessage",
      "successmessage",
      "documentTypes",
      "userDocuments",
    ]),
    author: function () {
      return (this.profile && this.profile.author) ? this.profile.author : {};
    },
    isKycVerified: function () {
      return this.author.is_kyc_verified === true;
    },
    uploadedDocuments: function () {
      return Array.isArray(this.userDocuments) ? this.userDocuments : [];
    },
    // GET kyc/document-types -> [{id, name, code, requires_back, order}]
    sortedTypes: function () {
      const list = Array.isArray(this.documentTypes) ? this.documentTypes.slice() : [];
      return list.sort(function (a, b) {
        return (a.order || 0) - (b.order || 0);
      });
    },
    document_options: function () {
      return this.sortedTypes.map(function (type) {
        // POST expects `document_type` as a string -> send the `code`
        return { value: type.code, text: type.name };
      });
    },
    selectedType: function () {
      const code = this.formData.document_type;
      if (!code) { return null; }
      const found = this.sortedTypes.filter(function (t) { return t.code === code; });
      return found.length ? found[0] : null;
    },
    // requires_back from the API drives whether the back image is mandatory
    backImageRequired: function () {
      return !!(this.selectedType && this.selectedType.requires_back);
    },
    isEditing: function () {
      return this.editingId !== null;
    },
    submitLabel: function () {
      if (this.isLoading) { return this.isEditing ? "Updating..." : "Uploading..."; }
      return this.isEditing ? "Update Document" : "Upload Document";
    },
  },
  created() {
    this.loadDocumentTypes();
    this.loadUploadedDocuments();
    if (!this.profile || !this.profile.author) {
      this.listProfile();
    }
  },
  methods: {
    ...mapActions([
      "listProfile",
      "listDocumentTypes",
      "listUserDocuments",
      "createUserDocument",
      "updateUserDocument",
      "removeUserDocument",
    ]),

    loadDocumentTypes() {
      this.typesLoading = true;
      const vm = this;
      return this.listDocumentTypes().then(function () {
        vm.typesLoading = false;
      });
    },

    loadUploadedDocuments() {
      return this.listUserDocuments();
    },

    /** document_type may come back as a code, an id, or a nested object */
    documentTypeLabel(doc) {
      const raw = doc.document_type;
      if (raw && typeof raw === "object") {
        return raw.name || raw.code || "Document";
      }
      const match = this.sortedTypes.filter(function (t) {
        return t.code === raw || String(t.id) === String(raw);
      });
      if (match.length) { return match[0].name; }
      if (doc.document_type_name) { return doc.document_type_name; }
      return raw || "Document";
    },

    documentStatus(doc) {
      if (doc.status) { return doc.status; }
      if (doc.is_verified === true) { return "verified"; }
      if (doc.is_verified === false) { return "pending"; }
      return "";
    },

    isDocVerified(doc) {
      if (doc.is_verified === true) { return true; }
      return String(doc.status || "").toLowerCase() === "verified";
    },

    /** Media paths may be relative - resolve against the API origin */
    resolveImage(path) {
      if (!path) { return ""; }
      if (/^https?:\/\//i.test(path) || /^data:/i.test(path)) { return path; }
      // VUE_APP_SERVICE_URL is relative in development (proxied), so media must
      // use its own absolute origin.
      const media = process.env.VUE_APP_MEDIA_URL || "";
      if (media) {
        return media.replace(/\/$/, "") + "/" + String(path).replace(/^\//, "");
      }
      const base = process.env.VUE_APP_SERVICE_URL || "";
      let origin = base;
      try {
        origin = new URL(base).origin;
      } catch (e) {
        origin = base.replace(/\/v1\/?$/, "");
      }
      return origin.replace(/\/$/, "") + "/" + String(path).replace(/^\//, "");
    },

    onFrontImageChange(event) {
      const file = event.target.files[0];
      if (file) {
        if (!this.validateImageFile(file)) {
          this.errors.front_image =
            "Invalid file type. Please upload JPG or PNG only.";
          this.formData.front_image = null;
          return;
        }
        this.errors.front_image = "";
        this.formData.front_image = file;
      }
    },

    onBackImageChange(event) {
      const file = event.target.files[0];
      if (file) {
        if (!this.validateImageFile(file)) {
          this.errors.back_image =
            "Invalid file type. Please upload JPG or PNG only.";
          this.formData.back_image = null;
          return;
        }
        this.errors.back_image = "";
        this.formData.back_image = file;
      }
    },

    validateImageFile(file) {
      const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
      return allowedTypes.indexOf(file.type) !== -1;
    },

    validateForm() {
      this.errors.document_type = "";
      this.errors.front_image = "";
      this.errors.back_image = "";

      if (!this.formData.document_type) {
        this.errors.document_type = "Document type is required";
      }
      // On create the front image is mandatory; on update it is optional
      // (an empty file input means "keep the existing image").
      if (!this.isEditing && !this.formData.front_image) {
        this.errors.front_image = "Front image is required";
      }
      if (!this.isEditing && this.backImageRequired && !this.formData.back_image) {
        this.errors.back_image = "Back image is required for this document type";
      }

      return !this.errors.document_type && !this.errors.front_image && !this.errors.back_image;
    },

    buildFormData() {
      const fd = new FormData();
      fd.append("document_type", this.formData.document_type);
      if (this.formData.document_number) {
        fd.append("document_number", this.formData.document_number);
      }
      if (this.formData.front_image) {
        fd.append("front_image", this.formData.front_image);
      }
      if (this.formData.back_image) {
        fd.append("back_image", this.formData.back_image);
      }
      return fd;
    },

    notify(msg, variant, title) {
      this.$bvToast.toast(msg, {
        title: title,
        variant: variant,
        solid: true,
        toaster: "b-toaster-top-center",
      });
    },

    /** CREATE + UPDATE */
    submitKYCDocument() {
      if (!this.validateForm()) {
        return;
      }
      this.isLoading = true;
      const vm = this;
      const fd = this.buildFormData();

      const request = this.isEditing
        ? this.updateUserDocument({ id: this.editingId, formData: fd })
        : this.createUserDocument(fd);

      return request
        .then(function () {
          if (vm.successmessage === "success") {
            vm.notify(
              vm.isEditing
                ? "Document updated successfully."
                : "Document uploaded successfully! Pending verification.",
              "success",
              "Success"
            );
            vm.resetForm();
            return vm.loadUploadedDocuments();
          }
          vm.notify(vm.message || "Failed to save document.", "danger", "Error");
        })
        .catch(function () {
          vm.notify("Failed to save document. Please try again.", "danger", "Error");
        })
        .then(function () {
          vm.isLoading = false;
        });
    },

    /** Load an existing document into the form */
    editDocument(doc) {
      const raw = doc.document_type;
      let code = raw;
      if (raw && typeof raw === "object") {
        code = raw.code;
      } else {
        const match = this.sortedTypes.filter(function (t) {
          return t.code === raw || String(t.id) === String(raw);
        });
        if (match.length) { code = match[0].code; }
      }
      this.editingId = doc.id;
      this.formData.document_type = code || null;
      this.formData.document_number = doc.document_number || "";
      this.formData.front_image = null;
      this.formData.back_image = null;
      this.clearFileInputs();
      this.errors = { document_type: "", front_image: "", back_image: "" };
      if (this.$el && this.$el.scrollIntoView) {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    },

    cancelEdit() {
      this.resetForm();
    },

    /** DELETE */
    deleteDocument(doc) {
      const confirmed = window.confirm(
        "Are you sure you want to delete this " + this.documentTypeLabel(doc) + " document?"
      );
      if (!confirmed) {
        return;
      }
      this.isLoading = true;
      const vm = this;
      return this.removeUserDocument(doc.id)
        .then(function () {
          if (vm.successmessage === "success") {
            vm.notify("Document deleted successfully.", "success", "Success");
            if (vm.editingId === doc.id) {
              vm.resetForm();
            }
            return vm.loadUploadedDocuments();
          }
          vm.notify(vm.message || "Failed to delete document.", "danger", "Error");
        })
        .catch(function () {
          vm.notify("Failed to delete document. Please try again.", "danger", "Error");
        })
        .then(function () {
          vm.isLoading = false;
        });
    },

    clearFileInputs() {
      if (this.$refs.frontImageFile) { this.$refs.frontImageFile.value = ""; }
      if (this.$refs.backImageFile) { this.$refs.backImageFile.value = ""; }
    },

    resetForm() {
      this.editingId = null;
      this.formData = {
        document_type: null,
        document_number: "",
        front_image: null,
        back_image: null,
      };
      this.errors = {
        document_type: "",
        front_image: "",
        back_image: "",
      };
      this.clearFileInputs();
    },
  },
};
</script>

<style scoped>
.document-image-container {
  border: 1px solid #ddd;
  padding: 10px;
  border-radius: 4px;
  display: inline-block;
  max-width: 100%;
}

.document-image-container img {
  max-height: 300px;
  object-fit: contain;
}

.card {
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 15px;
}

.card-header {
  background-color: #f5f5f5;
  padding: 12px 15px;
  border-bottom: 1px solid #ddd;
}

.card-header h6 {
  margin: 0;
  font-weight: 600;
  color: #333;
}

.card-body {
  padding: 15px;
}

.btn-danger {
  background-color: #dc3545;
  border-color: #dc3545;
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-danger:hover {
  background-color: #c82333;
  border-color: #bd2130;
}

.btn-danger:disabled {
  background-color: #ccc;
  border-color: #ccc;
  cursor: not-allowed;
  opacity: 0.6;
}

.btn-primary {
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.btn-primary:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.doc-status {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 10px;
  text-transform: capitalize;
  font-weight: 600;
}

.doc-status.is-verified {
  background-color: #e3f7e3;
  color: #00a800;
}

.doc-status.is-pending {
  background-color: #fdf3e0;
  color: #e08b00;
}

.material-icons-outlined {
  font-size: 18px;
  vertical-align: middle;
  margin-right: 5px;
}

.text-danger {
  color: #dc3545;
  font-size: 12px;
  margin-top: 5px;
  display: block;
}

.text-muted {
  color: #6c757d;
  font-size: 12px;
  margin-top: 5px;
  display: block;
}
</style>
